import { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);
const SESSION_MS = 8 * 60 * 60 * 1000;

export const ALL_PERMISSIONS = [
  { v: 'dashboard',       l: 'Dashboard',          group: 'Grundfunktionen' },
  { v: 'fuhrpark',        l: 'Fuhrpark anzeigen',  group: 'Fuhrpark'        },
  { v: 'fuhrpark_delete', l: 'Fuhrpark löschen',   group: 'Fuhrpark'        },
  { v: 'kalender',        l: 'Kalender',            group: 'Grundfunktionen' },
  { v: 'schaden',         l: 'Schadensmeldungen',   group: 'Grundfunktionen' },
  { v: 'packliste',       l: 'Packlisten',          group: 'Grundfunktionen' },
  { v: 'benutzer',        l: 'Benutzer anzeigen',  group: 'Verwaltung'      },
  { v: 'benutzer_manage', l: 'Benutzer verwalten', group: 'Verwaltung'      },
  { v: 'einstellungen',   l: 'Einstellungen',       group: 'Grundfunktionen' },
  { v: 'email_manage',    l: 'E-Mail Verwaltung',   group: 'Verwaltung'      },
  { v: 'news_manage',     l: 'News schreiben',      group: 'Verwaltung'      },
];

export const ROLE_DEFAULTS = {
  admin:       ALL_PERMISSIONS.map(p => p.v),
  benutzer:    ['dashboard','fuhrpark','kalender','schaden','packliste','einstellungen'],
  mitarbeiter: ['dashboard','kalender','schaden','einstellungen'],
};

const EXPLICIT_ONLY = ['email_manage','news_manage','benutzer_manage','fuhrpark_delete'];

function parsePerms(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
}

function getToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || null;
}

export function AuthProvider({ children }) {
  const [user, setUser]   = useState(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  function clearTimer() {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  }

  function doLogout(expired) {
    clearTimer();
    ['token','user','login_time','remember_me'].forEach(k => localStorage.removeItem(k));
    ['token','user','login_time'].forEach(k => sessionStorage.removeItem(k));
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    if (expired) { localStorage.setItem('session_expired','1'); window.location.href='/login'; }
  }

  function scheduleLogout(rememberMe, loginTime) {
    clearTimer();
    if (rememberMe) return;
    const ms = SESSION_MS - (Date.now() - (loginTime||Date.now()));
    if (ms <= 0) { doLogout(true); return; }
    timer.current = setTimeout(() => doLogout(true), ms);
  }

  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }

    const rememberMe = !!localStorage.getItem('remember_me');
    const loginTime  = parseInt(localStorage.getItem('login_time')||sessionStorage.getItem('login_time')||'0');

    if (!rememberMe && loginTime && Date.now()-loginTime > SESSION_MS) {
      doLogout(true); setLoading(false); return;
    }

    // WICHTIG: Token sofort setzen BEVOR /users/me aufgerufen wird
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    api.get('/users/me')
      .then(r => {
        const u = { ...r.data, permissions: parsePerms(r.data.permissions) };
        setUser(u);
        const store = rememberMe ? localStorage : sessionStorage;
        store.setItem('user', JSON.stringify(u));
        scheduleLogout(rememberMe, loginTime);
      })
      .catch(() => doLogout(false))
      .finally(() => setLoading(false));

    return clearTimer;
  }, []);

  const login = async (email, password, rememberMe = false) => {
    const { data } = await api.post('/auth/login', { email, password });
    const store = rememberMe ? localStorage : sessionStorage;
    store.setItem('token', data.token);
    store.setItem('login_time', String(Date.now()));
    api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    if (rememberMe) localStorage.setItem('remember_me','1');
    else localStorage.removeItem('remember_me');

    let u;
    try {
      const me = await api.get('/users/me');
      u = { ...me.data, permissions: parsePerms(me.data.permissions) };
    } catch {
      u = { ...data.user, permissions: parsePerms(data.user?.permissions) };
    }
    store.setItem('user', JSON.stringify(u));
    setUser(u);
    scheduleLogout(rememberMe, Date.now());
    return u;
  };

  const logout = () => doLogout(false);

  const updateUser = (d) => {
    const u = { ...d, permissions: parsePerms(d.permissions) };
    setUser(u);
    const store = localStorage.getItem('remember_me') ? localStorage : sessionStorage;
    store.setItem('user', JSON.stringify(u));
  };

  const refreshPermissions = async () => {
    try {
      const r = await api.get('/users/me');
      const u = { ...r.data, permissions: parsePerms(r.data.permissions) };
      setUser(u);
      const store = localStorage.getItem('remember_me') ? localStorage : sessionStorage;
      store.setItem('user', JSON.stringify(u));
      return u;
    } catch { return null; }
  };

  const can = (perm) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const dbPerms = parsePerms(user.permissions);
    if (EXPLICIT_ONLY.includes(perm)) return dbPerms.includes(perm);
    if (dbPerms.length > 0) return dbPerms.includes(perm);
    return (ROLE_DEFAULTS[user.role]||[]).includes(perm);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, refreshPermissions, loading, can, ALL_PERMISSIONS, ROLE_DEFAULTS }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
